/**
 * Online Food Ordering Mobile Bottom Navigation
 */

import {
  ShoppingCart as CartIcon,
  Home as HomeIcon,
  Restaurant as MenuIcon,
  Receipt as OrdersIcon,
  Person as ProfileIcon,
} from '@mui/icons-material';
import {
  Badge,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAppStore } from '@/store/useAppStore';
import { useAuthStore } from '@/store/useAuthStore';
import { useCartItemCount } from '@/store/useCartStore';

export default function MobileNav() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const toggleCartDrawer = useAppStore((state) => state.toggleCartDrawer);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const cartItemCount = useCartItemCount();

  // Hide on desktop
  if (!isMobile) {
    return null;
  }

  // Hide on checkout and admin pages
  if (location.pathname.startsWith('/checkout') || location.pathname.startsWith('/admin')) {
    return null;
  }

  // Get current route value
  const getValue = () => {
    const path = location.pathname;
    if (path === '/') return 0;
    if (path.startsWith('/menu')) return 1;
    // Cart now opens drawer, not a page - no selection state
    if (path.startsWith('/orders')) return 3;
    if (path === '/profile' || path === '/login') return 4;
    return -1;
  };

  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    switch (newValue) {
      case 0:
        navigate('/');
        break;
      case 1:
        navigate('/menu');
        break;
      case 2:
        toggleCartDrawer();
        break;
      case 3:
        navigate(isAuthenticated ? '/orders' : '/login');
        break;
      case 4:
        navigate(isAuthenticated ? '/profile' : '/login');
        break;
    }
  };

  return (
    <Paper
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: theme.zIndex.appBar,
        borderRadius: '20px 20px 0 0',
        overflow: 'hidden',
      }}
      elevation={8}
    >
      <BottomNavigation
        value={getValue()}
        onChange={handleChange}
        showLabels
        sx={{
          height: 56,
          '& .MuiBottomNavigationAction-root': {
            minWidth: 'auto',
            padding: '6px 0',
            '&.Mui-selected': {
              color: 'primary.main',
            },
          },
          '& .MuiBottomNavigationAction-label': {
            fontSize: '0.65rem',
            '&.Mui-selected': {
              fontSize: '0.7rem',
            },
          },
        }}
      >
        <BottomNavigationAction label={t('nav.home')} icon={<HomeIcon />} />
        <BottomNavigationAction label={t('nav.menu')} icon={<MenuIcon />} />
        <BottomNavigationAction
          label={t('cart.basket')}
          icon={
            <Badge badgeContent={cartItemCount} color="primary">
              <CartIcon />
            </Badge>
          }
        />
        <BottomNavigationAction label={t('nav.orders')} icon={<OrdersIcon />} />
        <BottomNavigationAction
          label={isAuthenticated ? t('nav.profile') : t('nav.sign_in')}
          icon={<ProfileIcon />}
        />
      </BottomNavigation>
    </Paper>
  );
}
